import { Content, isFilled } from '@prismicio/client';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';
import { PrismicNextLink } from '@prismicio/next';
import { FC } from 'react';
import { PlainRightArrow } from '@/assets/PlainRightArrow';
import StatItem from './StatItem';

export type HeroForVelxProps =
  SliceComponentProps<Content.HeroWithNavigationSlice>;

const HeroForVelx: FC<HeroForVelxProps> = ({ slice }) => {
  const { background_video, title, subtitle, primary_cta } = slice.primary;
  // `stats` is a custom field added in Prismic and may not be present in the generated types yet.
  // Use proper type casting to safely access it until the types are updated.
  const statsField = (slice.primary as unknown as { stats?: unknown[] }).stats;
  const stats = Array.isArray(statsField) ? statsField : undefined;

  // Server-rendered debug dump is used (if NODE_ENV === 'development') so
  // we don't need client-only hooks and avoid hydration mismatches.

  return (
    <section className="relative h-screen w-full overflow-hidden bg-[#0E1010]">
      {isFilled.linkToMedia(background_video) && (
        <video
          autoPlay
          loop
          muted
          playsInline
          preload="none"
          className="absolute inset-0 z-0 w-full h-full object-cover object-top md:object-center"
        >
          <source src={background_video.url} type="video/webm" />
          Your browser does not support the video tag.
        </video>
      )}

      {/* Overlay negro */}
      <div className="absolute inset-0 z-10 bg-black bg-opacity-50" />

      {/* Gradiente que funde el video con el siguiente slice */}
      <div className="absolute bottom-0 left-0 w-full h-32 md:h-48 z-20 bg-gradient-to-b from-transparent to-[#0E1010] pointer-events-none" />

      <div className="relative z-30 flex h-full flex-col text-white px-[30px] md:px-[100px] pt-[148px] pb-[46px]">
        <main className="container mx-auto flex flex-grow flex-col items-center justify-center text-center">
          <PrismicRichText
            field={title}
            components={{
              heading1: ({ children }) => (
                <h1 className="h1-hero text-center font-antonio tracking-normal">
                  {children}
                </h1>
              ),
            }}
          />
          <PrismicRichText
            field={subtitle}
            components={{
              paragraph: ({ children }) => (
                <p className="mt-4 text-[20px] md:text-[24px] font-medium leading-[1.4] text-center text-gray-300 font-inter">
                  {children}
                </p>
              ),
            }}
          />
          <div className="mt-5">
            {primary_cta?.text && isFilled.link(primary_cta) && (
              <PrismicNextLink
                field={primary_cta}
                className="group flex items-center px-6 py-2.5 text-base font-bold leading-relaxed tracking-normal text-black hover:bg-green-600 transition-colors"
                style={{
                  borderRadius: '8px',
                  background: '#05F273',
                }}
              >
                {primary_cta.text}
                <PlainRightArrow
                  className="ml-2 transition-transform group-hover:translate-x-1"
                  height={20}
                  width={20}
                  arrowFill="black"
                />
              </PrismicNextLink>
            )}
          </div>
          {/* Stats (2-3 items) */}
          {Array.isArray(stats) && stats.length > 0 && (
            <div className="mt-8 flex justify-center">
              <div className="flex flex-wrap items-center justify-center gap-4">
                {stats
                  .filter(
                    (item): item is Record<string, unknown> =>
                      typeof item === 'object' && item !== null,
                  )
                  .map((item, idx) => (
                    <StatItem key={idx} item={item} />
                  ))}
              </div>
            </div>
          )}
        </main>
      </div>
    </section>
  );
};

export default HeroForVelx;
